import BaseModel from './BaseModel';
import TktInstance from './TktInstance';
import Availability from './Availability';
import Application from './Application';
import { Utils } from '../services';
import moment from 'moment';

/** Alternate list tabs — same read rights as the parent list, not CRUD actions. */
const LIST_VIEW_TAB_ACTIONS = {
    pricinglists: ['pricings', 'lists'],
    bucketlists: ['buckets', 'lists'],
    tickettypes: ['windows', 'pricings', 'full'],
};

/** Class representing an user. */
class User extends BaseModel {
    constructor(properties) {
        super(properties);

        // True when the API payload should include tkt_instance_ids (e.g. admin user form).
        // False when ids were only derived for in-app convenience — sending them on PUT would
        // trigger kuser set_instance_memberships and wipe other instance memberships.
        this._explicit_tkt_instance_ids = Boolean(
            properties && Object.prototype.hasOwnProperty.call(properties, 'tkt_instance_ids')
        );

        if (this.tkt_instance) {
            this.tkt_instance = new TktInstance(this.tkt_instance);
        }

        if (Array.isArray(this.tkt_instance_ids) && this.tkt_instance_ids.length > 0) {
            this.tkt_instance_ids = [...this.tkt_instance_ids];
        } else if (this.tkt_instance_id) {
            this.tkt_instance_ids = [this.tkt_instance_id];
        } else {
            this.tkt_instance_ids = [];
        }

        // ensure settings is an object
        if (Array.isArray(this.settings))
            this.settings = Object.assign({}, this.settings);

        if (this.availabilities)
            this.availabilities = (this.availabilities || []).map(props => new Availability(props));

        if (this.applications)
            this.applications = (this.applications || []).map(props => new Application(props));
    }

    prepareForUpdate() {
        const prepared = super.prepareForUpdate();
        if (!this._explicit_tkt_instance_ids) {
            delete prepared.tkt_instance_ids;
        }
        // Not a Kore column; sent in JSON it makes KUser::update_properties throw (400).
        delete prepared._explicit_tkt_instance_ids;
        return prepared;
    }

    avatarUrl() {
        if (!this.avatar)
            return null;

        return Utils.imgProxyUrl(`${this.avatar}?t=${moment(this.updated_at).toISOString()}`);
    }

    /** Superadmin is reserved to Net Oxygen staff emails. */
    static isNetOxygenEmail = (email) => {
        if (!email || typeof email !== 'string')
            return false;
        return email.trim().toLowerCase().endsWith('@netoxygen.ch');
    };

    isSuperAdmin = () => {
        if (!User.isNetOxygenEmail(this.email))
            return false;
        return !!this.abilities?.superadmin || !!this.is_super_admin;
    };

    can = (action, mod) => {
        if (!this.abilities)
            return false;
        if (this.isSuperAdmin())
            return true;

        // Read any
        if (action === 'read')
            return (this.abilities[mod]?.readOther || this.abilities[mod]?.readSelf)

        // Specific right
        if (this.abilities?.[mod]?.[action])
            return true;
        return false;
    }

    verifyAccessSections = (section) => {
        if (this.isSuperAdmin())
            return true;
        switch (section) {
            case 'operation':
                return this.canAccessOperation();
            case 'admin':
                return this.canAccessAdmin(section);
            case 'analysis':
                return this.canAccessAnalysis();
            default:
                return false;
        }
    }

    canAccessOperation = () => {
        return this.anyRightInSection('operation');
    }

    canAccessAdmin = () => {
        return this.anyRightInSection('admin');
    }

    canAccessAnalysis = () => {
        return this.can('readOther', 'stats');
    }

    anyRightInSection = (section) => {
        if (!this.abilities)
            return false;
        let result = false;
        Utils.getTopbarModules(section).forEach(mod => {
            if ((mod in this.abilities) && mod !== 'abilities_templates')
                Object.keys(this.abilities[mod]).forEach(ability => {
                    if (!result && this.abilities[mod][ability]) {
                        result = true;
                    }
                });
        });
        return result;
    }

    canAccess = (pathname) => {
        // Superadmin always gets his ways
        if (this.abilities?.superadmin)
            return true;

        if (!pathname)
            return false;

        let [, controller, action, params] = pathname.split("/", 4);  // path array kronos.com [0] / module [1] / action [2]

        if (controller === 'analysis' && action === 'system' && params === 'logs')
            return this.isSuperAdmin() || this.can('readOther', 'logs');

        if (controller === 'analysis' && action === 'email_campaigns')
            return this.isSuperAdmin() || this.can('readOther', 'logs');

        // Workflow runs live under /operation/workflows/:workflowId/runs...
        // Only workflow_runs rights apply (workflows:* is for admin /workflows).
        if (controller === 'operation' && action === 'workflows') {
            return this.can('read', 'workflow_runs');
        }

        if ((controller === 'devices') && (
            this.can('readOther', 'devices')
            || this.can('editOther', 'devices')
            || this.can('deleteOther', 'devices')
        ))
            return true;

        // Same *Other rights as the ability matrix (not in MONGO_RESSOURCES).
        if ((controller === 'equipment_orders') && (
            this.can('readOther', 'equipment_orders')
            || this.can('create', 'equipment_orders')
            || this.can('editOther', 'equipment_orders')
            || this.can('deleteOther', 'equipment_orders')
        ))
            return true;

        if ((controller === 'configuration_qr_codes') && this.can('readOther', 'configuration_qr_codes'))
            return true;

        if (controller === 'plan') {
            if (action === 'shift')
                return !!this.can('read', 'plan');
            return !!(this.can('read', 'plan') || this.can('read', 'tasks'));
        }

        // Séances (Exploitation) — droits du module screenings, pas programmations
        if (controller === 'screenings') {
            if (action === 'edit' || action === 'show' || action === 'new') {
                const ability_action = action === 'new' ? 'create'
                    : (action === 'edit' ? 'editOther' : 'readOther');
                return this.can(ability_action, 'screenings');
            }
            return this.can('read', 'screenings');
        }

        if (controller === 'equipment_catalog' && this.isSuperAdmin())
            return true;

        if (controller === 'equipment_planning')
            return this.isSuperAdmin();

        // postgres screenings exception
        if (controller === 'programmation_groups')
            controller = "programmations";

        const instance_modules = this.tkt_instance?.options?.modules;
        const modules_for_utils = Array.isArray(instance_modules)
            ? [...instance_modules]
            : [];
        const currentModules = Utils.getCurrentModules(modules_for_utils);

        if (controller === 'reservations' && this.can('read', 'screenings'))
            return true;

        if (controller === 'bookingstatus' && this.can('read', 'screenings'))
            return true;

        //If instance doesn't have modules, don't even try the rest + dashboards exceptions
        if (!currentModules.includes(controller) && controller !== "admin" && controller !== "operation" && controller !== "analysis" && controller !== "")
            return false;

        // URL /layouts is the PDF editor; rights live on pdf_layouts.
        if (controller === 'layouts')
            controller = 'pdf_layouts';

        //Stats only has read
        if ((controller === 'stats' || controller === 'analysis') && this.can('readOther', 'stats'))
            return true;

        if (controller === 'users' && ['edit', 'show'].includes(action) && this.id === params)
            return true;

        // events/event_type/actions/id exception
        // `/events/:type/all` is a list deeplink (EventsList "All" tab), not a CRUD action named "all".
        if (action && ['movie', 'music_group', 'drama', 'exhibition', 'service'].includes(action))
            action = params === 'all' ? undefined : params;

        if (action && LIST_VIEW_TAB_ACTIONS[controller]?.includes(action)) {
            return this.can(
                Utils.getMongoResources().includes(controller) ? 'readOther' : 'readSelf',
                controller
            );
        }

        if (action) {
            // www.kronos.com/basepath/actionpath
            switch (action) {
                case 'edit':
                    //TktInstance exception for abilities templates
                    if (controller === "tktInstances" && this.can('edit_templates', 'users') && !this.can('editSelf', 'tktInstances')) {
                        controller = 'users';
                        action = 'edit_templates';
                    } else {
                        if (Utils.getMongoResources().includes(controller))
                            action = 'editOther';
                        else
                            action = 'editSelf';
                    }
                    break;
                case 'show':
                case 'view':
                    if (Utils.getMongoResources().includes(controller))
                        action = 'readOther';
                    else
                        action = 'readSelf';
                    break;
                case 'new':
                        action = 'create';
                    break;
                default:
                    if (Utils.getMongoResources().includes(controller))
                        action += 'Other';
                    else
                        action += 'Self';
            }
            return this.can(action, controller);
        } else {

            // site root
            if (!controller)
                return true;

            if (controller === 'admin' || controller === 'operation')
                return this.verifyAccessSections(controller);

            // Message only has create
            if (controller === "messages" && this.can("create", "messages"))
                return true;

            // list fallback (kronos/screenings for example)
            if (this.can((Utils.getMongoResources().includes(controller) ? "readOther" : "readSelf"), controller))
                return true;
        }
        return false;
    }
}

User.GENDER_DEFAULT = '?';
User.GENDER_FEMALE  = 'F';
User.GENDER_MALE    = 'M';

User.genders = () => {
    return [
        User.GENDER_DEFAULT,
        User.GENDER_FEMALE,
        User.GENDER_MALE,
    ];
};

/**
 * Get the fields to get server side when we
 * ask for a listing
 */
User.getListingFields = () => [
    'id', 'created_by', 'avatar', 'firstname', 'lastname', 'email', 'fullname', 'rfullname', 'activities', 'tkt_instance_id'
];

export default User;
